import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
} from '@material-ui/core';
import { ToggleButton } from '@material-ui/lab';
import SearchIcon from '@material-ui/icons/Search';

type TriState = 'all' | 'yes' | 'no';
import { Alert } from '@material-ui/lab';
import {
  Content,
  Header,
  ItemCardGrid,
  ItemCardHeader,
  Page,
  Progress,
  WarningPanel,
} from '@backstage/core-components';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';

type ResourceObject = {
  apiVersion?: string;
  kind?: string;
  metadata?: { name?: string; namespace?: string };
  spec?: { serve?: boolean; [key: string]: any };
};

const DIGANDO_API_PREFIX = 'platform.digando.io';

const useCardStyles = makeStyles(theme => ({
  card: {
    minHeight: 280,
    display: 'flex',
    flexDirection: 'column',
    '& [class*="BackstageItemCardHeader-root"]': {
      backgroundSize: 'auto !important',
    },
  },
  content: {
    flex: 1,
  },
  tags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
  },
  serveToggle: {
    padding: '3px 9px',
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.text.primary,
    border: `1px solid ${
      theme.palette.type === 'dark'
        ? 'rgba(255, 255, 255, 0.23)'
        : 'rgba(0, 0, 0, 0.23)'
    }`,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&.Mui-selected, &.Mui-selected:hover': {
      backgroundColor: theme.palette.success.main,
      color: theme.palette.success.contrastText,
      borderColor: theme.palette.success.main,
    },
  },
}));

const parseApi = (api?: string) => {
  if (!api) return undefined;
  const parts = api.split('/');
  if (parts.length < 3) return undefined;
  return { group: parts[0], version: parts[1], plural: parts[2] };
};

const ResourceCard = ({ entity }: { entity: Entity }) => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const classes = useCardStyles();
  const obj = (entity.spec as any)?.object as ResourceObject | undefined;
  const apiInfo = parseApi((entity.spec as any)?.api);
  const initial = Boolean(obj?.spec?.serve);
  const [serve, setServe] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const canPatch = Boolean(apiInfo && obj?.metadata?.name);

  const onToggle = async () => {
    if (!canPatch) {
      setErr('Resource information unavailable');
      return;
    }
    const next = !serve;
    setBusy(true);
    setErr(null);
    setServe(next);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('development');
      const res = await fetchApi.fetch(`${baseUrl}/serve`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          group: apiInfo!.group,
          version: apiInfo!.version,
          plural: apiInfo!.plural,
          namespace: obj!.metadata?.namespace,
          name: obj!.metadata!.name,
          serve: next,
        }),
      });
      if (!res.ok) {
        const text = await res.text();
        let message = text;
        try {
          const json = JSON.parse(text);
          message = json?.error || json?.message || text;
        } catch {
          /* not json */
        }
        throw new Error(message);
      }
      const data = await res.json().catch(() => ({}));
      const actual = typeof data?.serve === 'boolean' ? data.serve : next;
      setServe(actual);
      if (actual !== next) {
        setErr(`Server reported spec.serve=${actual}; toggle reverted`);
      }
    } catch (e: any) {
      setServe(!next);
      setErr(e?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  };

  const title = obj?.metadata?.name || entity.metadata.name;
  const subtitle = obj?.kind ? `${obj.kind} · ${obj.metadata?.namespace ?? 'cluster'}` : '';

  const tags = entity.metadata.tags ?? [];

  return (
    <>
      <Card className={classes.card}>
        <CardMedia>
          <ItemCardHeader title={title} subtitle={subtitle} />
        </CardMedia>
        <CardContent className={classes.content}>
          {entity.metadata.description && (
            <Typography variant="body2" color="textSecondary" paragraph>
              {entity.metadata.description}
            </Typography>
          )}
          {tags.length > 0 && (
            <div className={classes.tags}>
              {tags.map(tag => (
                <Chip key={tag} label={tag} size="small" variant="outlined" />
              ))}
            </div>
          )}
        </CardContent>
        <Divider style={{ marginLeft: 16, marginRight: 16 }} />
        <CardActions style={{ padding: '12px 16px' }}>
          <Grid container alignItems="center" spacing={1} justifyContent="flex-end">
            <Grid item>
              <Button
                size="small"
                variant="outlined"
                component={RouterLink}
                to={`/catalog/${entity.metadata.namespace || 'default'}/resource/${entity.metadata.name}`}
              >
                View
              </Button>
            </Grid>
            {busy && (
              <Grid item>
                <CircularProgress size={16} />
              </Grid>
            )}
            <Grid item>
              <ToggleButton
                size="small"
                value="serve"
                selected={serve}
                onChange={onToggle}
                disabled={busy || !canPatch}
                classes={{ root: classes.serveToggle }}
              >
                SERVE
              </ToggleButton>
            </Grid>
          </Grid>
        </CardActions>
      </Card>
      <Snackbar
        open={!!err}
        autoHideDuration={6000}
        onClose={() => setErr(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="error" onClose={() => setErr(null)}>
          {err}
        </Alert>
      </Snackbar>
    </>
  );
};

export const DevelopmentPage = () => {
  const catalog = useApi(catalogApiRef);
  const [entities, setEntities] = useState<Entity[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [syncFilter, setSyncFilter] = useState<TriState>('all');
  const [readyFilter, setReadyFilter] = useState<TriState>('all');

  useEffect(() => {
    let cancelled = false;
    catalog
      .getEntities({ filter: { kind: 'Resource' } })
      .then(res => {
        if (cancelled) return;
        const filtered = res.items.filter(e => {
          const api = (e.spec as any)?.api as string | undefined;
          const type = (e.spec as any)?.type as string | undefined;
          return api?.startsWith(DIGANDO_API_PREFIX) && type?.endsWith('Claim');
        });
        setEntities(filtered);
      })
      .catch(e => !cancelled && setError(String(e)));
    return () => {
      cancelled = true;
    };
  }, [catalog]);

  const visibleEntities = useMemo(() => {
    if (!entities) return null;
    const q = search.trim().toLowerCase();
    return entities
      .filter(e => {
        const obj = (e.spec as any)?.object as ResourceObject | undefined;
        const name = obj?.metadata?.name || e.metadata.name;
        const kind = obj?.kind || (e.spec as any)?.type;
        const ns = obj?.metadata?.namespace ?? '';
        const tags = e.metadata.tags ?? [];
        const isSynced = tags.includes('synced');
        const isReady = tags.includes('ready');
        if (q && !([name, kind, ns].some(v => v?.toLowerCase().includes(q)))) return false;
        if (syncFilter === 'yes' && !isSynced) return false;
        if (syncFilter === 'no' && isSynced) return false;
        if (readyFilter === 'yes' && !isReady) return false;
        if (readyFilter === 'no' && isReady) return false;
        return true;
      })
      .sort((a, b) => {
        const an =
          ((a.spec as any)?.object as ResourceObject | undefined)?.metadata?.name ||
          a.metadata.name;
        const bn =
          ((b.spec as any)?.object as ResourceObject | undefined)?.metadata?.name ||
          b.metadata.name;
        return an.localeCompare(bn);
      });
  }, [entities, search, syncFilter, readyFilter]);

  return (
    <Page themeId="tool">
      <Header title="Development" subtitle="Toggle serve state per platform resource" />
      <Content>
        {error && <WarningPanel title="Failed to load components" message={error} />}
        {!entities && !error && <Progress />}
        {entities && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={2}>
              <div style={{ minHeight: 56, marginBottom: 8 }} />
              <Grid container direction="column" spacing={2}>
                <Grid item>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder="Search"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item>
                  <FormControl fullWidth variant="outlined" size="small">
                    <InputLabel id="sync-filter-label">Sync</InputLabel>
                    <Select
                      labelId="sync-filter-label"
                      label="Sync"
                      value={syncFilter}
                      onChange={e => setSyncFilter(e.target.value as TriState)}
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="yes">Synced</MenuItem>
                      <MenuItem value="no">Not synced</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item>
                  <FormControl fullWidth variant="outlined" size="small">
                    <InputLabel id="ready-filter-label">Ready</InputLabel>
                    <Select
                      labelId="ready-filter-label"
                      label="Ready"
                      value={readyFilter}
                      onChange={e => setReadyFilter(e.target.value as TriState)}
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="yes">Ready</MenuItem>
                      <MenuItem value="no">Not ready</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={10}>
              <Typography
                variant="h5"
                component="h2"
                gutterBottom
                style={{ minHeight: 56, display: 'flex', alignItems: 'center' }}
              >
                Applications
              </Typography>
              {visibleEntities && visibleEntities.length === 0 ? (
                <Typography>
                  {entities.length === 0
                    ? 'No platform.digando.io resources discovered.'
                    : 'No resources match the current search.'}
                </Typography>
              ) : (
                <ItemCardGrid>
                  {visibleEntities!.map(entity => (
                    <ResourceCard
                      key={`${entity.metadata.namespace}/${entity.metadata.name}`}
                      entity={entity}
                    />
                  ))}
                </ItemCardGrid>
              )}
            </Grid>
          </Grid>
        )}
      </Content>
    </Page>
  );
};
